export type GatheringMechanism = 'app_user' | 'link'

export interface Perspective {
  readonly id: string
  readonly key: string
  readonly title: string
  readonly nameLong: string
  readonly nameShort: string
  readonly requestFeedbackDescription?: string
  readonly icon: string
  readonly allowedMechanisms: ReadonlyArray<GatheringMechanism>
  /** Transitional — ignore; removed after migration */
  readonly legacyMethod?: 'peer' | 'external'
}

export interface GatheringEntry {
  readonly perspectiveKey: string
  readonly mechanism: GatheringMechanism
  readonly memberIds?: ReadonlyArray<string>
  readonly enabled: boolean
}

/** UI state for collecting members / open links per perspective */
export type PerspectiveGatheringState = {
  enabled: boolean
  appUserMemberIds: string[]
  useOpenLink: boolean
}

export const emptyPerspectiveGatheringState = (): PerspectiveGatheringState => ({
  enabled: true,
  appUserMemberIds: [],
  useOpenLink: false,
})

export const SELF_PERSPECTIVE_KEY = 'self'

/** Synthetic self perspective — not in CMS; used when a topic has `includeSelf`. */
export const SELF_PERSPECTIVE: Perspective = {
  id: SELF_PERSPECTIVE_KEY,
  key: SELF_PERSPECTIVE_KEY,
  title: 'Self',
  nameLong: 'Self|Self|Self',
  nameShort: 'Self|Self|Self',
  icon: 'account_circle',
  allowedMechanisms: ['app_user'],
}

/**
 * Full perspective list for a topic: optional self + CMS perspectives.
 * Use wherever analysis keys need to be resolved to Perspective objects.
 */
export const perspectivesFromTopic = (topic: {
  includeSelf: boolean
  perspectives: ReadonlyArray<Perspective>
}): Perspective[] => {
  if (topic.includeSelf) {
    return [SELF_PERSPECTIVE, ...topic.perspectives]
  }
  return [...topic.perspectives]
}

/** Perspective keys that count when combining topics — self is excluded. */
export const nonSelfPerspectiveKeys = (
  perspectives: ReadonlyArray<Pick<Perspective, 'key'>>,
): Set<string> =>
  new Set(
    perspectives
      .map((perspective) => perspective.key)
      .filter((key) => key !== SELF_PERSPECTIVE_KEY),
  )

export const intersectNonSelfPerspectiveKeys = (
  topics: ReadonlyArray<{ perspectives: ReadonlyArray<Pick<Perspective, 'key'>> }>,
): Set<string> => {
  if (topics.length === 0) return new Set()
  return topics
    .map((topic) => nonSelfPerspectiveKeys(topic.perspectives))
    .reduce((intersection, keys) => new Set([...intersection].filter((key) => keys.has(key))))
}
